import { Buffer } from "buffer";
import type { Connection, PartiallyDecodedInstruction } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { KNOWN_POOLS } from "../data/knownPools";
import {
  POOL_ACCOUNT_DISCRIMINATOR,
  POOL_ACCOUNT_SIZE,
  PROGRAM_ID,
} from "./constants";
import type { PoolRegistryEntry } from "./types";

const SWAP_DISCRIMINATOR = "f8c69e91e17587c8";
const SWAP_WITH_REFERRAL_DISCRIMINATOR = "d03ab89ebe57c5c5";
const CREATE_TOKEN_DISCRIMINATOR = "5434cce4188cea4b";
const CACHE_KEY = "tribe-trader:discovered-pools:v1";
const REGISTRY_TTL_MS = 5 * 60_000;
let memoryRegistry: { entries: PoolRegistryEntry[]; loadedAt: number } | undefined;

function rejectAfter(milliseconds: number): Promise<never> {
  return new Promise((_, reject) => {
    window.setTimeout(
      () => reject(new Error("The RPC timed out while looking for new pools")),
      milliseconds,
    );
  });
}

function isPoolData(data: Buffer): boolean {
  return (
    data.length === POOL_ACCOUNT_SIZE &&
    bytesToHex(data.subarray(0, 8)) === POOL_ACCOUNT_DISCRIMINATOR
  );
}

function bytesToHex(data: Uint8Array): string {
  return Array.from(data, (value) => value.toString(16).padStart(2, "0")).join("");
}

function loadDiscoveryCache(): PoolRegistryEntry[] {
  try {
    const value = localStorage.getItem(CACHE_KEY);
    return value ? (JSON.parse(value) as PoolRegistryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveDiscoveryCache(entries: PoolRegistryEntry[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entries));
  } catch {
    // Discovery still works when storage is unavailable.
  }
}

async function resolveUnknownPool(
  connection: Connection,
  pool: PublicKey,
  creator: PublicKey,
  mint: PublicKey,
): Promise<PoolRegistryEntry | undefined> {
  const { default: bs58 } = await import("bs58");
  const signatures = await connection.getSignaturesForAddress(pool, { limit: 8 });
  for (const signature of signatures) {
    if (signature.err) continue;
    const transaction = await connection.getParsedTransaction(signature.signature, {
      commitment: "confirmed",
      maxSupportedTransactionVersion: 0,
    });
    if (!transaction) continue;
    for (const instruction of transaction.transaction.message.instructions) {
      if (!("data" in instruction) || !instruction.programId.equals(PROGRAM_ID)) continue;
      const discriminator = Buffer.from(bs58.decode(instruction.data))
        .subarray(0, 8);
      const discriminatorHex = bytesToHex(discriminator);
      let solIndex: number;
      let tokenIndex: number;
      if (discriminatorHex === CREATE_TOKEN_DISCRIMINATOR) {
        solIndex = 7;
        tokenIndex = 6;
      } else if (
        discriminatorHex === SWAP_DISCRIMINATOR ||
        discriminatorHex === SWAP_WITH_REFERRAL_DISCRIMINATOR
      ) {
        solIndex = 4;
        tokenIndex = 5;
      } else {
        continue;
      }
      const partial = instruction as PartiallyDecodedInstruction;
      if (!partial.accounts[solIndex] || !partial.accounts[tokenIndex]) continue;
      return {
        pool: pool.toBase58(),
        creator: creator.toBase58(),
        mint: mint.toBase58(),
        solVault: partial.accounts[solIndex].toBase58(),
        tokenVault: partial.accounts[tokenIndex].toBase58(),
      };
    }
  }
  return undefined;
}

export async function discoverPoolRegistry(
  connection: Connection,
): Promise<PoolRegistryEntry[]> {
  if (memoryRegistry && Date.now() - memoryRegistry.loadedAt < REGISTRY_TTL_MS) {
    return memoryRegistry.entries;
  }
  const registry = new Map(
    [...KNOWN_POOLS, ...loadDiscoveryCache()].map((entry) => [entry.pool, entry]),
  );
  try {
    const accounts = await Promise.race([
      connection.getProgramAccounts(PROGRAM_ID, {
        commitment: "confirmed",
        filters: [{ dataSize: POOL_ACCOUNT_SIZE }],
      }),
      rejectAfter(2_500),
    ]);
    const unknown = accounts.filter(
      ({ pubkey, account }) =>
        isPoolData(account.data) && !registry.has(pubkey.toBase58()),
    );
    // New launches are rare. Resolve sequentially so a public RPC is not flooded.
    for (const { pubkey, account } of unknown) {
      const creator = new PublicKey(account.data.subarray(16, 48));
      const mint = new PublicKey(account.data.subarray(48, 80));
      const entry = await resolveUnknownPool(connection, pubkey, creator, mint);
      if (entry) registry.set(entry.pool, entry);
    }
    saveDiscoveryCache([...registry.values()]);
  } catch {
    // The bundled on-chain snapshot keeps the UI usable on rate-limited RPCs.
  }
  const entries = [...registry.values()];
  memoryRegistry = { entries, loadedAt: Date.now() };
  return entries;
}

// Referenced by tests to prevent accidental discriminator regressions.
export const DISCOVERY_DISCRIMINATORS = {
  swap: SWAP_DISCRIMINATOR,
  swapWithReferral: SWAP_WITH_REFERRAL_DISCRIMINATOR,
  createToken: CREATE_TOKEN_DISCRIMINATOR,
} as const;
